/**
 * Shared E→A match-state classifier for the senior list view.
 *
 * Single source of truth for BOTH:
 *   - the E→A field colors rendered in qa-list.tsx (ExpectedActualDiff), and
 *   - the server-side `match[]` list filter (applySeniorListFilterSort),
 * so the filter can never disagree with the colors seniors see on screen.
 *
 * Units compare via normalizeUnit (shared/normalization.ts): "Suite 1110" and
 * "1110" are the same unit — prefix words (suite/apt/unit/#/floor…), buzzer
 * instructions, and building-unit hyphens are stripped before comparing.
 * Street numbers and DZ codes compare on the lightweight base normalization
 * (trim / lowercase / collapse whitespace / strip leading #), matching the
 * comparison the list has always used for those fields.
 *
 * Deliberately independent of attention_flag (shared/attention-flag.ts) —
 * attention derivation is slated for an overhaul; this classifier only
 * describes what the E→A pairs literally show.
 */
import { normalizeBase, normalizeUnit } from "./normalization";

export type FieldKind = "street" | "unit" | "dz";

export type FieldMatchState =
  | "match"       // both sides present and equal after normalization
  | "mismatch"    // both sides present and different
  | "undetected"  // exactly one side present (nothing to compare against)
  | "absent";     // neither side present — field is not rendered

export type RowMatchState = "mismatch" | "all-match" | "undetected";

export const ROW_MATCH_STATES: readonly RowMatchState[] = ["mismatch", "all-match", "undetected"];

/** Normalize one side of a pair for comparison/presence, per field kind. */
export function normalizeMatchField(kind: FieldKind, value: string | null | undefined): string {
  if (kind === "unit") return normalizeUnit(value);
  return normalizeBase(value).replace(/^#/, "");
}

/** Classify one expected/actual pair. */
export function classifyField(
  kind: FieldKind,
  expected: string | null | undefined,
  actual: string | null | undefined,
): FieldMatchState {
  const e = normalizeMatchField(kind, expected);
  const a = normalizeMatchField(kind, actual);
  if (!e && !a) return "absent";
  if (e && a) return e === a ? "match" : "mismatch";
  return "undetected";
}

export interface ExpectedActualPairs {
  street: [string | null, string | null];
  unit:   [string | null, string | null];
  dz:     [string | null, string | null];
}

/**
 * Row-level bucket, precedence mismatch > undetected > all-match:
 *   - "mismatch"   — at least one field is red
 *   - "undetected" — no red, but at least one field is missing a side
 *   - "all-match"  — every rendered field is green
 * Returns null when no field has any value at all (e.g. pickup tasks) — such
 * rows are excluded whenever the match filter is active.
 */
export function classifyRow(pairs: ExpectedActualPairs): RowMatchState | null {
  const states = [
    classifyField("street", pairs.street[0], pairs.street[1]),
    classifyField("unit",   pairs.unit[0],   pairs.unit[1]),
    classifyField("dz",     pairs.dz[0],     pairs.dz[1]),
  ];
  if (states.includes("mismatch"))   return "mismatch";
  if (states.includes("undetected")) return "undetected";
  if (states.includes("match"))      return "all-match";
  return null;
}
